/**
 * First- vs third-party classification (docs/event-model.md §5): a request is
 * first-party when its host is on the same *site* as the page. Without a
 * public suffix list, the site is approximated as the last two labels of the
 * host (`cdn.shop.example` → `shop.example`), which is exact for the
 * reserved `*.example` domains the examples use.
 */
import { parseUrl } from "./index";

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/** The registrable-domain approximation of a hostname. */
export function siteOf(host: string): string {
  const lower = host.toLowerCase();
  if (IPV4.test(lower) || lower.startsWith("[")) return lower;
  const labels = lower.split(".");
  return labels.slice(-2).join(".");
}

/** The lowercased hostname of an absolute URL with a special scheme, or null. */
export function hostOf(url: string): string | null {
  const parts = parseUrl(url);
  return parts && parts.hostname !== "" ? parts.hostname : null;
}

/** Whether `url` is on a different site than `pageUrl`; null if either has no host. */
export function isThirdParty(url: string, pageUrl: string): boolean | null {
  const host = hostOf(url);
  const page = hostOf(pageUrl);
  if (host === null || page === null) return null;
  return siteOf(host) !== siteOf(page);
}
